<template>
    <div class="store-fields" :class="{ 'single-column': !!appStoreListing || !!providerSignUp }">
        <c-select
            v-model="storeTypeModel"
            label="Ecommerce Platform"
            :options="storeTypeOptions"
            :model-transform="valueReducer"
            :clearable="false"
            :disabled="lockStoreType"
        />

        <!-- Amazon stores are identified by name; every other platform by URL -->
        <validation-provider
            v-if="!appStoreListing && !providerSignUp"
            v-slot="{ errors }"
            :rules="isAmazon ? 'required|amazon-store-name' : 'required|url'"
            :name="isAmazon ? 'Store Name' : 'Ecommerce URL'"
        >
            <c-input
                v-model="storeUrlModel"
                :label="isAmazon ? 'Store Name*' : 'Ecommerce URL*'"
                :placeholder="urlPlaceholder"
                :error="!!errors[0]"
                :error-msg="errors[0]"
            />
        </validation-provider>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { ValidationProvider } from 'vee-validate';
import CInput from '@/components/shared/CInput/CInput.vue';
import CSelect, { valueReducer } from '@/components/shared/CSelect';
import { appStoreListings, providerSignUps, storeTypeOptions } from '@/views/SignUp/storeTypes';

const props = defineProps<{
    storeType: string;
    storeUrl: string;
    lockStoreType?: boolean;
}>();

const emit = defineEmits<{
    (event: 'update:storeType', value: string): void;
    (event: 'update:storeUrl', value: string): void;
}>();

const storeTypeModel = computed({
    get: () => props.storeType,
    set: (value: string) => emit('update:storeType', value)
});

const storeUrlModel = computed({
    get: () => props.storeUrl,
    set: (value: string) => emit('update:storeUrl', value)
});

const appStoreListing = computed(() => appStoreListings[props.storeType]);
const providerSignUp = computed(() => providerSignUps[props.storeType]);
const isAmazon = computed(() => props.storeType === 'Amazon');

const urlPlaceholder = computed(() => {
    switch (props.storeType) {
        case 'Amazon':
            return 'Store Name';
        case 'Etsy':
            return 'www.etsy.com/shop/YourShopName';
        default:
            return 'Ecommerce URL';
    }
});
</script>

<style lang="scss" scoped>
.store-fields {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
    &.single-column {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 575px) {
    .store-fields {
        grid-template-columns: 1fr;
    }
}
</style>
